<script setup lang="ts" generic="T, U extends TKiliDataTableDefinition<any>">
import { useMutation } from "@tanstack/vue-query";
import { debounce } from "es-toolkit";
import { isEmpty } from "es-toolkit/compat";
import { UnwrapRef, ref, computed, onMounted, getCurrentInstance } from "vue";

import { usePaginatedDataTable } from "@/components/ui/paginated-data-table/use-paginated-data-table";
import { type TKiliDataTableDefinition } from "@/data/api/kili/common/datatable/data-table-definition";
import { EKiliSortFilterDirection } from "@/data/api/kili/common/datatable/data-table-request.type";
import {
  TKiliDataTableRequestParams,
  IKiliDataTableSearchRequestBody,
  type TKiliDataTableRequestResponse,
} from "@/data/api/kili/common/datatable/data-table-request.type";
import { KiliNullFilter, EKiliFilterType } from "@/data/api/kili/common/filters/kili-filter";
import { toastKiliError } from "@/data/api/kili/common/toast-kili-error";
import { keyOf, TranslationKey, useTypedIntl } from "@/locales/typed-intl";
import $date from "@/utils/date";
import { resolvePath, getDifferences } from "@/utils/object";
import { translateBoolToYesOrNo } from "@/utils/translation-utils";

import Column from "primevue/column";
import DataTable, { DataTableRowEditSaveEvent, DataTableSortEvent } from "primevue/datatable";
import IconField from "primevue/iconfield";
import InputNumber from "primevue/inputnumber";
import InputText from "primevue/inputtext";
import Paginator, { type PageState } from "primevue/paginator";
import ProgressSpinner from "primevue/progressspinner";

import HospitalityButton from "@/components/ui/buttons/hospitality-button.vue";
import KiliDataTableReaderDropdownFilter from "@/components/ui/paginated-data-table/filters/kili-data-table-reader-dropdown-filter.vue";
import KiliDataTableReaderNullFilter from "@/components/ui/paginated-data-table/filters/kili-data-table-reader-null-filter.vue";

const DEFAULT_ROWS_COUNT = 20;
const DEFAULT_PAGE = 0;

type Item = T extends (infer Item)[] ? Item : never;
type TFilterValue = {
  key: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
};

interface IProps {
  datatable: U;
  title?: string;
  rows?: number;
  page?: number;
  isLoading?: boolean;
  refreshable?: boolean;
  // eslint-disable-next-line vue/require-default-prop
  searchParams?: TKiliDataTableRequestParams;
  // eslint-disable-next-line vue/require-default-prop
  searchQueryKeyId?: number;
  selectionMode?: "single" | "multiple";
  editMode?: "row";
}

interface IEmits {
  (e: "submit-filters-search"): void;
  (e: "page-change"): void;
  (e: "click:row", row: Item): void;
  (e: "row-edit-init"): void;
  (e: "row-edited"): void;
  (e: "search-error", error: unknown): void;
}

defineExpose({ search });

const selection = defineModel<T | T[]>("selection", { required: false });
const editingRows = defineModel<T[]>("editingRows", { default: [] });

const editedRowId = ref<boolean>();

const { t, k } = useTypedIntl();

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const data = ref<TKiliDataTableRequestResponse<any>>({
  rows: [],
  invalidFilters: [],
  totalRows: 0,
});

const props = withDefaults(defineProps<IProps>(), {
  rows: DEFAULT_ROWS_COUNT,
  page: DEFAULT_PAGE,
  selectionMode: undefined,
  editMode: undefined,
  title: undefined,
});

const cachedPageState = ref<PageState>({
  page: props.page,
  rows: props.rows,
  first: 1,
});

const filters = ref(getInitialFilters());
const sorts = ref<Record<string, EKiliSortFilterDirection>>({});

const formattedFilters = computed(() => {
  return Object.entries(filters.value)
    .map(([key, filter]) => {
      let filterDefinition = props.datatable.filterableFields[key];
      if (!filterDefinition && props.datatable.additionalFilters) {
        filterDefinition = props.datatable.additionalFilters[key];
      }

      return {
        name: filter.key,
        operator: filterDefinition.filter.filterType.defaultOperator ?? null,
        value: filter.value,
      };
    })
    .filter((f) => !(f.operator === KiliNullFilter.defaultOperator && !f.value))
    .filter((f) => f.value !== null && `${f.value}`.length > 0);
});

const formattedSorts = computed(() => {
  return Object.entries(sorts.value).map(([key, direction]) => {
    return {
      fieldName: key,
      direction,
    };
  });
});

const { mutateAsync: searchData, isPending } = useMutation({
  mutationKey: props.datatable.searchMutationKey(props.searchQueryKeyId),
  mutationFn: async () => {
    let body: IKiliDataTableSearchRequestBody = {
      filters: formattedFilters.value,
      sorts: formattedSorts.value,
      page: cachedPageState.value.page,
      pageSize: cachedPageState.value.rows,
    };

    if (props.searchParams) {
      body = {
        ...body,
        params: { ...props.searchParams },
      };
    }

    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (data.value = (await props.datatable.search(body)) as TKiliDataTableRequestResponse<any>);
    } catch (error) {
      emit("search-error", error);
    }
  },
  onError: (error) => {
    toastKiliError(error);
  },
});

const { mutateAsync: editRow, isPending: isEditRowPending } = useMutation({
  mutationKey: props.datatable.editRowOpts!.editRowMutationKey(editedRowId.value),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mutationFn: async (body: any) => {
    return await props.datatable.editRowOpts!.editFn(body);
  },
  onSuccess: () => {
    search();
    emit("row-edited");
  },
  onError: (error) => {
    toastKiliError(error, undefined, (propertyPath) => {
      return t(props.datatable.headers[propertyPath!].label);
    });
  },
});

const isShowingFilters = defineModel<boolean>("isShowingFilters", {
  default: false,
});

const emit = defineEmits<IEmits>();

const { rowsPerPageOptions } = usePaginatedDataTable();

function search() {
  searchData();
}

onMounted(() => {
  search();
});

const values = computed(() => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return data.value.rows as any[];
});

function treatColumnValue<T>(value: T) {
  if (value instanceof Date) {
    return $date.formatDateWithoutTimezone(value);
  }

  if (typeof value === "boolean") {
    return translateBoolToYesOrNo(value);
  }

  if (value === null || value === undefined) {
    return "-";
  }

  return `${value}`.trim().length > 0 ? value : "-";
}

function getInitialFiltersFromDefinition(
  filters?: typeof props.datatable.filterableFields | typeof props.datatable.additionalFilters,
): Record<string, TFilterValue> {
  if (!filters) {
    return {};
  }

  return Object.entries(filters).reduce((prev, [key, value]) => {
    return {
      ...prev,
      [key]: {
        key: value!.key,
        value: value.initialValue ?? null,
      },
    };
  }, {});
}

function getInitialFilters() {
  return {
    ...getInitialFiltersFromDefinition(props.datatable.filterableFields),
    ...getInitialFiltersFromDefinition(props.datatable.additionalFilters),
  };
}

function getClearFiltersFromDefinition(
  filters?: typeof props.datatable.filterableFields | typeof props.datatable.additionalFilters,
): Record<string, TFilterValue> {
  if (!filters) {
    return {};
  }

  return Object.entries(filters).reduce((prev, [key, value]) => {
    return {
      ...prev,
      [key]: {
        key: value!.key,
        value: null,
      },
    };
  }, {});
}

function generateClearFilters() {
  return {
    ...getClearFiltersFromDefinition(props.datatable.filterableFields),
    ...getClearFiltersFromDefinition(props.datatable.additionalFilters),
  };
}

function clearFilters() {
  filters.value = generateClearFilters() as UnwrapRef<Record<string, TFilterValue>>;
}

function handlePageChanged(state: PageState) {
  cachedPageState.value = state;
  search();
  emit("page-change");
}

function handleSubmitFiltersSearch() {
  search();
  emit("submit-filters-search");
}

const debounceSubmitFiltersSearch = debounce(async () => {
  handleSubmitFiltersSearch();
}, 800);

/**
 * Small hack as vue lacks of functionality to handle properly generics
 */
const headers = computed(() => {
  return Object.entries(props.datatable.headers).map(([key, value]) => ({
    ...value,
    key: key,
  }));
});

const columnFilters = computed(() => {
  return (
    Object.entries(props.datatable.filterableFields)
      // eslint-disable-next-line
      .filter(([_, field]) => field !== undefined)
      .map(([key, value]) => {
        const header = headers.value.find((header) => header.key === key);
        if (!header) {
          return undefined;
        }

        return {
          ...value,
          header: header,
        };
      })
      .filter(Boolean) as Array<
      (typeof props.datatable.filterableFields)[string] & { header: (typeof headers.value)[number] }
    >
  );
});

const additionalFilters = computed(() => {
  if (props.datatable.additionalFilters) {
    return (
      Object.entries(props.datatable.additionalFilters)
        // eslint-disable-next-line
        .filter(([_, field]) => field !== undefined)
        .map(([key, value]) => {
          return {
            ...value,
            header: {
              key,
              label: value.label,
            },
          };
        }) as Array<
        (typeof props.datatable.additionalFilters)[string] & { header: { key: string; label: TranslationKey } }
      >
    );
  }

  return [];
});

const filterableFields = computed(() => {
  return [...columnFilters.value, ...additionalFilters.value];
});

const hasOnClickRowEventListener = computed(() => {
  return getCurrentInstance()?.vnode?.props?.["onClick:row"] !== undefined;
});

function handleSortEvent(event: DataTableSortEvent) {
  // single sort only for now
  if (!event.sortField) {
    sorts.value = {};
    handleSubmitFiltersSearch();
    return;
  }

  if (typeof event.sortField !== "string") {
    return;
  }

  if (event.sortOrder === 1) {
    sorts.value = {
      [event.sortField]: EKiliSortFilterDirection.ASC,
    };
  } else if (event.sortOrder === -1) {
    sorts.value = {
      [event.sortField]: EKiliSortFilterDirection.DESC,
    };
  }

  handleSubmitFiltersSearch();
}

function handleRowEdit(event: DataTableRowEditSaveEvent) {
  if (!props.datatable.editRowOpts) {
    return;
  }

  if (isEditRowPending.value) {
    return;
  }

  const changes = getDifferences(event.data, event.newData);

  if (isEmpty(changes)) {
    return;
  }

  editedRowId.value = event.data[props.datatable.editRowOpts.editKeyId];
  editRow({
    [props.datatable.editRowOpts.editKeyId]: event.data[props.datatable.editRowOpts.editKeyId],
    ...changes,
  });
}
</script>

<template>
  <div
    ref="container"
    class="kili-paginated-data-table-container h-full w-full"
  >
    <DataTable
      v-model:editing-rows="editingRows"
      v-model:selection="selection"
      class="kili-paginated-data-table"
      data-test-id="data-table-reader"
      :edit-mode="props.editMode"
      lazy
      :loading="isPending || props.isLoading || isEditRowPending"
      :pt="{
        headerRow: {
          class: 'kili-paginated-data-table-head-row',
        },
        tbody: {
          class: 'kili-paginated-data-table-tbody',
        },
        bodyRow: {
          class: hasOnClickRowEventListener && 'cursor-pointer hover:bg-brand/5',
        },
      }"
      removable-sort
      scroll-height="flex"
      scrollable
      size="small"
      :total-records="data.totalRows"
      :value="values"
      @page="handlePageChanged($event)"
      @row-click="$emit('click:row', $event.data)"
      @row-edit-init="$emit('row-edit-init')"
      @row-edit-save="handleRowEdit($event)"
      @sort="handleSortEvent($event)"
    >
      <template #header>
        <div
          :class="{
            'flex flex-row-reverse justify-between items-center pb-4': filterableFields.length === 1,
          }"
        >
          <div class="flex space-x-4">
            <div class="flex-1 space-x-4 whitespace-nowrap">
              <HospitalityButton
                v-if="props.refreshable"
                :aria-label="t(k.global.misc.refresh) + ' ' + props.title"
                data-test-id="refresh-button"
                is-icon-only
                :label="t(k.global.misc.refresh) + ' ' + props.title"
                :loading="isPending || isEditRowPending"
                severity="tertiary"
                size="xs"
                @click="search()"
              >
                <i class="pi pi-replay" />
              </HospitalityButton>
              <slot
                :filters="formattedFilters"
                name="actions"
              />
              <HospitalityButton
                v-if="filterableFields.length > 1"
                :aria-label="t(k.global.filters[isShowingFilters ? 'hide' : 'show'])"
                data-test-id="display-filters-button"
                is-icon-only
                :label="t(k.global.filters[isShowingFilters ? 'hide' : 'show'])"
                severity="tertiary"
                size="xs"
                @click="isShowingFilters = !isShowingFilters"
              >
                <i class="`pi pi-filter${isFilterEmpty ? '' : '-fill'}`" />
              </HospitalityButton>
              <HospitalityButton
                v-if="isShowingFilters"
                data-test-id="clear-filters-button"
                is-icon-only
                severity="tertiary"
                size="xs"
                @click="clearFilters"
              >
                <i class="pi pi-filter-slash" />
              </HospitalityButton>
              <HospitalityButton
                v-if="isShowingFilters"
                data-test-id="search-filters-button"
                is-icon-only
                size="xs"
                @click="handleSubmitFiltersSearch"
              >
                <i class="pi pi-search" />
              </HospitalityButton>
            </div>

            <div class="space-x-4 whitespace-nowrap">
              <slot name="right-actions" />
            </div>
          </div>

          <div
            v-show="isShowingFilters || filterableFields.length === 1"
            class="filters"
            :class="{
              'mt-2': isShowingFilters,
            }"
          >
            <div class="flex flex-wrap items-baseline filters-inputs-container">
              <slot name="pre-filters" />
              <slot name="filters">
                <template
                  v-for="filterableField in filterableFields"
                  :key="filterableField.key"
                >
                  <slot :name="`filter-${filterableField.header.key}`">
                    <div class="flex flex-col gap-2">
                      <label v-if="!filterableField.placeholder">
                        {{ t(filterableField.label ?? filterableField.header.label) }}
                      </label>
                      <IconField
                        v-if="filterableField.filter.filterType.type === EKiliFilterType.ID"
                        class="relative"
                      >
                        <InputIcon
                          v-if="filterableField.icon"
                          :class="`pi ${filterableField.icon} absolute top-[12px] left-[14px]`"
                        />
                        <InputNumber
                          v-model="filters[filterableField.header.key].value"
                          class="max-w-[10rem]"
                          :data-test-id="`${filterableField.header.key}-filter-input`"
                          :placeholder="filterableField.placeholder ? t(filterableField.placeholder) : ''"
                          @input="debounceSubmitFiltersSearch"
                          @submit="handleSubmitFiltersSearch"
                        />
                      </IconField>
                      <IconField
                        v-else-if="filterableField.filter.filterType.type === EKiliFilterType.STRING"
                        class="relative"
                      >
                        <InputIcon
                          v-if="filterableField.icon"
                          :class="`pi ${filterableField.icon} absolute top-[12px] left-[14px]`"
                        />
                        <InputText
                          v-model.trim="filters[filterableField.header.key].value"
                          :data-test-id="`${filterableField.header.key}-filter-input`"
                          :placeholder="filterableField.placeholder ? t(filterableField.placeholder) : ''"
                          @input="debounceSubmitFiltersSearch"
                          @submit="handleSubmitFiltersSearch"
                        />
                      </IconField>
                      <IconField
                        v-else-if="filterableField.filter.filterType.type === EKiliFilterType.PATH"
                        class="relative"
                      >
                        <InputIcon
                          v-if="filterableField.icon"
                          :class="`pi ${filterableField.icon} absolute top-[12px] left-[14px]`"
                        />
                        <InputText
                          v-model.trim="filters[filterableField.header.key].value"
                          :data-test-id="`${filterableField.header.key}-filter-input`"
                          :placeholder="filterableField.placeholder ? t(filterableField.placeholder) : ''"
                          @input="debounceSubmitFiltersSearch"
                          @submit="handleSubmitFiltersSearch"
                        />
                      </IconField>
                      <KiliDataTableReaderDropdownFilter
                        v-else-if="filterableField.filter.filterType.type === EKiliFilterType.DROPDOWN_BY_ID"
                        v-model="filters[filterableField.header.key].value"
                        :data-test-id="`${filterableField.header.key}-filter-dropdown`"
                        filter-on="label"
                        :options="filterableField.filter.options"
                        :placeholder="filterableField.placeholder ? t(filterableField.placeholder) : ''"
                        @change="handleSubmitFiltersSearch"
                      />
                      <KiliDataTableReaderNullFilter
                        v-else-if="filterableField.filter.filterType.type === EKiliFilterType.NULL"
                        v-model="filters[filterableField.header.key].value"
                        :data-test-id="`${filterableField.header.key}-filter-input`"
                        :label="t(filterableField.label ?? filterableField.header.label)"
                        @change="handleSubmitFiltersSearch"
                      />
                    </div>
                  </slot>
                </template>
              </slot>
              <slot name="post-filters" />
            </div>
          </div>
        </div>
      </template>

      <template #empty>
        <div class="flex justify-center items-center min-h-[55vh] space-x-4">
          <span>{{ t(k.global.misc.noResult) }}</span>
        </div>
      </template>

      <template #loading>
        <ProgressSpinner />
      </template>

      <Column
        v-if="props.selectionMode"
        align-frozen="left"
        class="p-4"
        data-test-id="select-checkbox"
        frozen
        :selection-mode="props.selectionMode"
      />

      <template
        v-for="header in headers"
        :key="header.key"
      >
        <Column
          :field="header.key"
          :header="t(header.label)"
          :sortable="header.sortable"
          style="min-width: 150px"
        >
          <template #body="row">
            <slot
              :name="header.key"
              :row="row"
            >
              <span v-if="header.formatter">
                <template v-if="header.formatter(resolvePath(row.data as any, header.key)).opts">
                  {{
                    $t(
                      keyOf(header.formatter(resolvePath(row.data as any, header.key)).translationPath),
                      header.formatter(resolvePath(row.data as any, header.key)).opts!!,
                    )
                  }}
                </template>
                <template v-else>
                  {{ t(header.formatter(resolvePath(row.data as any, header.key)).translationPath as TranslationKey) }}
                </template>
              </span>
              <span v-else>
                {{ treatColumnValue(resolvePath(row.data as any, header.key)) }}
              </span>
            </slot>
          </template>
          <template
            v-if="header.editable"
            #editor="{ data: value, field, editorSaveCallback }"
          >
            <InputNumber
              v-if="header.editable.type === 'number'"
              v-model="value[field]"
              :data-test-id="`${field}-number-input`"
              fluid
              @keydown.enter="editorSaveCallback"
            />

            <InputText
              v-else-if="header.editable.type === 'string'"
              v-model="value[field]"
              :data-test-id="`${field}-text-input`"
              fluid
              @keydown.enter="editorSaveCallback"
            />
          </template>
        </Column>
      </template>
      <slot name="additional-columns" />

      <Column
        v-if="props.editMode === 'row'"
        class="!text-center"
        :row-editor="true"
      >
        <template #roweditoriniticon>
          <div
            class="flex gap-2 text-brand px-2"
            data-test-id="enable-edit-row-button"
          >
            <i class="pi pi-pencil" />
            <span class="font-semibold">{{ t(k.global.misc.edit) }}</span>
          </div>
        </template>
        <template #roweditorsaveicon>
          <i
            class="pi pi-check text-green-500 outline outline-1 outline-gray-300 rounded-full p-2 text-sm"
            data-test-id="submit-edit-row-button"
          />
        </template>
        <template #roweditorcancelicon>
          <i
            class="pi pi-times text-red-500 outline outline-1 outline-gray-300 rounded-full p-2 text-sm"
            data-test-id="cancel-edit-row-button"
          />
        </template>
      </Column>

      <template #footer>
        <Paginator
          data-test-id="paginator"
          :rows="rowsPerPageOptions[0]"
          :rows-per-page-options="rowsPerPageOptions"
          :total-records="data.totalRows"
          @page="handlePageChanged($event)"
        />
      </template>
    </DataTable>
  </div>
</template>

<style lang="scss">
.kili-paginated-data-table-head-row {
  th {
    @apply h-[60px];
  }
}

.kili-paginated-data-table-tbody {
  tr {
    @apply h-[60px];
  }
}

.kili-paginated-data-table {
  .p-datatable-wrapper {
    @apply overflow-auto;
  }

  .filters {
    &-inputs-container > * {
      @apply mr-4;
    }

    .p-dropdown-label {
      @apply text-left;
    }
  }

  .p-paginator-element {
    @apply min-w-[2rem] h-8;
  }

  .p-paginator-rpp-options {
    height: 2rem;

    & > span {
      line-height: 1rem;
      font-size: 0.75rem;
      padding-top: 0.5rem;
    }
  }

  .select-filter > .p-float-label > .p-dropdown {
    @apply min-w-[10rem];
  }

  .action-button {
    @apply p-0;
    @apply w-6 h-6;

    &.p-button-secondary:not(:hover) {
      @apply bg-transparent;
      @apply border-none;
    }
  }

  .p-datatable-row-editor-init {
    width: unset;

    &:active {
      @apply bg-transparent !important;
    }
  }
}
</style>
